/**
SELLERIE MARINE - SERVICE ADMINISTRATION

📋 DESCRIPTION:
Service TypeScript pour la gestion des fonctionnalités d'administration.
Interface complète pour CRUD utilisateurs, statistiques système et monitoring.

🎯 RESPONSABILITÉS:
- Gestion complète des utilisateurs (création, modification, suppression)
- Récupération des statistiques système et monitoring
- Gestion des logs d'activité et audit
- Configuration système et maintenance
- Interface sécurisée avec vérification des droits admin

🔗 DÉPENDANCES:
- ApiService: Service de base pour les requêtes HTTP
- AuthContext: Vérification des droits administrateur
- Types: Interfaces TypeScript pour la cohérence des données

📡 INTERACTIONS:
- Backend: Routes /api/admin/* sécurisées
- Frontend: Composants d'administration
- Stockage: Gestion des tokens et sessions admin
- Monitoring: Logs et statistiques en temps réel

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Toutes les requêtes incluent les headers d'authentification
- Gestion d'erreurs robuste avec messages utilisateur
- Cache intelligent pour les données fréquemment consultées
- Interface type-safe avec TypeScript complet
*/

import { apiService } from './ApiService';

// Types pour l'administration
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager' | 'user';
  userId: string;
  companyName?: string;
  phone?: string | null;
  isBeta?: boolean;
  isConfirmed?: boolean;
  isActive?: boolean;
  status?: string;
  subscriptionType?: 'standard' | 'premium';
  subscriptionStatus?: string | null;
  subscriptionEndDate?: string | null;
  trialStartedAt?: string | null;
  trialEndAt?: string | null;
  trialUsed?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastLoginAt?: string | null;
  lastActivityAt?: string | null;
  devisCountTotal?: number;
  articlesCountTotal?: number;
  clientsCountTotal?: number;
  loginCountTotal?: number;
  accessOverride?: string | null;
}

export interface CreateUserData {
  email: string;
  name: string;
  role?: 'admin' | 'manager' | 'user';
  subscription_type?: 'standard' | 'premium';
  companyName: string;
  is_beta?: boolean;
  subscription_end_date?: string | null;
  phone?: string | null;
}

export interface UpdateUserData {
  name?: string;
  password?: string;
  role?: 'admin' | 'manager' | 'user';
  subscription_type?: 'standard' | 'premium';
  subscription_status?: string | null;
  companyName?: string;
  is_beta?: boolean;
  subscription_end_date?: string | null;
  trial_started_at?: string | null;
  trial_end_at?: string | null;
  trial_used?: boolean;
  is_active?: boolean;
  phone?: string | null;
  is_confirmed?: boolean;
}

export interface AdminUsersPage {
  items: AdminUser[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  sort?: {
    sortBy?: string | null;
    sortDir?: 'asc' | 'desc';
  };
}

export interface UserEvent {
  id?: number;
  eventType: string;
  createdAt?: string | null;
  metadata?: Record<string, any>;
  tenantId?: number | null;
  userId?: number | null;
  actorUserId?: number | null;
}

export interface UserActivityCounts {
  loginCount30d?: number;
  devisCount30d?: number;
  pricingCount30d?: number;
  exportPdfCount30d?: number;
  optimizationCount30d?: number;
}

export interface UserActivitySummary {
  lastLoginAt?: string | null;
  lastActivityAt?: string | null;
  counts30d?: UserActivityCounts;
}

export interface UserUsage {
  devisCountTotal: number;
  counts30d?: UserActivityCounts;
}

export interface AdminUserDetail {
  user: AdminUser;
  activitySummary: UserActivitySummary;
  usage: UserUsage;
  recentEvents: UserEvent[];
}

export interface SystemStats {
  users: {
    total: number;
    by_role: Record<string, number>;
  };
  data: Record<string, number>;
  system: {
    uptime: string;
    version: string;
    environment: string;
  };
  generated_at: string;
}

export interface SystemLog {
  timestamp: string;
  level: 'INFO' | 'WARNING' | 'ERROR';
  message: string;
  module: string;
}

export interface SystemConfig {
  app: {
    name: string;
    version: string;
    maintenance_mode: boolean;
  };
  features: {
    premium_features_enabled: boolean;
    pdf_generation_enabled: boolean;
    email_notifications_enabled: boolean;
  };
  limits: {
    max_users: number;
    max_devis_per_user: number;
    max_file_upload_size: string;
  };
}

export interface AdminApiResponse<T = any> {
  success: boolean;
  data?: T;
  users?: Record<string, AdminUser>;
  items?: any;
  total?: number;
  stats?: SystemStats;
  logs?: SystemLog[];
  config?: SystemConfig;
  message?: string;
  error?: string;
}

class AdminService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = apiService.getBaseUrl();
  }

  /**
   * Récupère les headers d'authentification pour les requêtes admin
   */
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  /**
   * Effectue une requête HTTP sécurisée vers l'API admin
   */
  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<AdminApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}/admin${endpoint}`, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Erreur AdminService ${endpoint}:`, error);
      throw error;
    }
  }

  private normalizeUser(user: any, emailFallback?: string): AdminUser {
    return {
      id: user.id,
      email: user.email || emailFallback || '',
      name: user.name,
      role: user.role,
      userId: user.userId || user.user_id,
      companyName: user.companyName ?? user.company_name,
      phone: user.phone ?? null,
      isBeta: user.isBeta ?? user.is_beta,
      isConfirmed: user.isConfirmed ?? user.is_confirmed,
      isActive: user.isActive ?? user.is_active,
      status: user.status,
      subscriptionType: user.subscriptionType ?? user.subscription_type,
      subscriptionStatus: user.subscriptionStatus ?? user.subscription_status,
      subscriptionEndDate: user.subscriptionEndDate ?? user.subscription_end_date,
      trialStartedAt: user.trialStartedAt ?? user.trial_started_at,
      trialEndAt: user.trialEndAt ?? user.trial_end_at,
      trialUsed: user.trialUsed ?? user.trial_used,
      createdAt: user.createdAt ?? user.created_at,
      updatedAt: user.updatedAt ?? user.updated_at,
      lastLoginAt: user.lastLoginAt ?? user.last_login_at ?? user.lastLogin,
      lastActivityAt: user.lastActivityAt ?? user.last_activity_at,
      devisCountTotal: user.devisCountTotal ?? user.devis_count_total ?? 0,
      articlesCountTotal: user.articlesCountTotal ?? user.articles_count_total ?? 0,
      clientsCountTotal: user.clientsCountTotal ?? user.clients_count_total ?? 0,
      loginCountTotal: user.loginCountTotal ?? user.login_count_total ?? 0,
      accessOverride: user.accessOverride ?? user.access_override ?? null,
    };
  }

  // ==================== GESTION DES UTILISATEURS ====================

  /**
   * Récupère tous les utilisateurs
   */
  async getUsers(params: {
    search?: string;
    role?: string;
    status?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
  } = {}): Promise<AdminUsersPage> {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    if (params.role && params.role !== 'all') searchParams.append('role', params.role);
    if (params.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params.page) searchParams.append('page', String(params.page));
    if (params.pageSize) searchParams.append('pageSize', String(params.pageSize));
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortDir) searchParams.append('sortDir', params.sortDir);
    const query = searchParams.toString();

    const response = await this.makeRequest<any>(`/users${query ? `?${query}` : ''}`);

    const itemsRaw =
      response?.data?.items ??
      response?.items ??
      (response?.users
        ? Object.entries(response.users).map(([email, user]: any) => ({ ...user, email }))
        : []);

    const items = Array.isArray(itemsRaw) ? itemsRaw.map((u: any) => this.normalizeUser(u, u.email)) : [];

    const page = response?.data?.page ?? Number(params.page || 1);
    const pageSize = response?.data?.pageSize ?? Number(params.pageSize || 20);
    const total = response?.data?.total ?? response?.total ?? items.length;
    const totalPages =
      response?.data?.totalPages ??
      (pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1);
    const sortPayload = response?.data?.sort ?? {};
    const sortInfo =
      sortPayload.sortBy ||
      sortPayload.sortDir ||
      params.sortBy ||
      params.sortDir
        ? {
            sortBy: (sortPayload.sortBy ?? params.sortBy) as string | null | undefined,
            sortDir: (sortPayload.sortDir ?? params.sortDir) as 'asc' | 'desc' | undefined,
          }
        : undefined;

    return { items, page, pageSize, total, totalPages, sort: sortInfo };
  }

  async getUserDetail(userId: string | number): Promise<AdminUserDetail> {
    const response = await this.makeRequest<any>(`/users/${encodeURIComponent(String(userId))}`);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Erreur lors du chargement du detail utilisateur');
    }

    const payload = response.data;
    const rawUser = payload.user || payload;
    const user = this.normalizeUser(rawUser, rawUser?.email);

    const activityRaw = payload.activitySummary || payload.activity_summary || {};
    const usageRaw = payload.usage || payload.usageSummary || {};
    const usage: UserUsage = {
      devisCountTotal: usageRaw.devisCountTotal ?? usageRaw.devis_count_total ?? 0,
      counts30d: usageRaw.counts30d ?? activityRaw.counts30d,
    };

    const recentEventsRaw = payload.recentEvents || payload.events || [];
    const recentEvents: UserEvent[] = Array.isArray(recentEventsRaw)
      ? recentEventsRaw
          .map((evt: any) => ({
            id: evt.id,
            eventType: evt.eventType || evt.event_type || '',
            createdAt: evt.createdAt ?? evt.created_at ?? null,
            metadata: evt.metadata ?? evt.event_metadata ?? evt.meta ?? {},
            tenantId: evt.tenantId ?? evt.tenant_id ?? null,
            userId: evt.userId ?? evt.user_id ?? null,
            actorUserId: evt.actorUserId ?? evt.actor_user_id ?? null,
          }))
          .filter((evt) => !!evt.eventType)
      : [];

    return {
      user,
      activitySummary: {
        lastLoginAt: activityRaw.lastLoginAt ?? activityRaw.last_login_at ?? null,
        lastActivityAt: activityRaw.lastActivityAt ?? activityRaw.last_activity_at ?? null,
        counts30d: usage.counts30d,
      },
      usage,
      recentEvents,
    };
  }

  /**
   * Crée un nouvel utilisateur
   */
  async createUser(userData: CreateUserData): Promise<AdminApiResponse<AdminUser>> {
    const response = await this.makeRequest<AdminUser>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Erreur lors de la création de l\'utilisateur');
    }

    return response;
  }

  /**
   * Met à jour un utilisateur existant
   */
  async updateUser(email: string, userData: UpdateUserData): Promise<AdminUser> {
    const response = await this.makeRequest<AdminUser>(`/users/${encodeURIComponent(email)}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Erreur lors de la mise à jour de l\'utilisateur');
    }

    return response.data;
  }

  /**
   * Met à jour le type d'abonnement d'un utilisateur (Standard/Premium)
   */
  async updateUserSubscription(userId: string, subscriptionType: 'standard' | 'premium', subscriptionEndDate?: string | null): Promise<void> {
    await this.makeRequest(`/users/${encodeURIComponent(userId)}/subscription`, {
      method: 'PUT',
      body: JSON.stringify({ subscription_type: subscriptionType, subscription_end_date: subscriptionEndDate ?? null }),
    });
  }

  /**
   * Supprime un utilisateur
   */
  async deleteUser(email: string): Promise<void> {
    const response = await this.makeRequest(`/users/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    });

    if (!response.success) {
      throw new Error(response.error || 'Erreur lors de la suppression de l\'utilisateur');
    }
  }

  /**
   * Rejoue le seed defaults pour un utilisateur (mode admin)
   */
  async seedDefaults(userId: string): Promise<any> {
    const response = await this.makeRequest(`/users/${encodeURIComponent(userId)}/seed_defaults`, {
      method: 'POST',
      body: JSON.stringify({ force_seeded_fields: true }),
    });

    if (!response.success) {
      throw new Error(response.error || 'Erreur lors du seed defaults');
    }

    return response.data;
  }

  // ==================== STATISTIQUES SYSTÈME ====================

  /**
   * Récupère les statistiques du système
   */
  async getSystemStats(): Promise<SystemStats> {
    const response = await this.makeRequest<SystemStats>('/stats');

    if (!response.success || !response.stats) {
      throw new Error(response.error || 'Erreur lors de la récupération des statistiques');
    }

    return response.stats;
  }

  // ==================== LOGS SYSTÈME ====================

  /**
   * Récupère les logs système
   */
  async getSystemLogs(options: {
    level?: 'INFO' | 'WARNING' | 'ERROR';
    limit?: number;
  } = {}): Promise<SystemLog[]> {
    const params = new URLSearchParams();
    if (options.level) params.append('level', options.level);
    if (options.limit) params.append('limit', options.limit.toString());

    const queryString = params.toString();
    const endpoint = queryString ? `/logs?${queryString}` : '/logs';

    const response = await this.makeRequest<SystemLog[]>(endpoint);

    if (!response.success || !response.logs) {
      throw new Error(response.error || 'Erreur lors de la récupération des logs');
    }

    return response.logs;
  }

  // ==================== CONFIGURATION SYSTÈME ====================

  /**
   * Récupère la configuration système
   */
  async getSystemConfig(): Promise<SystemConfig> {
    const response = await this.makeRequest<SystemConfig>('/config');

    if (!response.success || !response.config) {
      throw new Error(response.error || 'Erreur lors de la récupération de la configuration');
    }

    return response.config;
  }

  /**
   * Met à jour la configuration système
   */
  async updateSystemConfig(config: Partial<SystemConfig>): Promise<SystemConfig> {
    const response = await this.makeRequest<SystemConfig>('/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });

    if (!response.success || !response.config) {
      throw new Error(response.error || 'Erreur lors de la mise à jour de la configuration');
    }

    return response.config;
  }

  // ==================== MAINTENANCE ====================

  /**
   * Crée une sauvegarde des données
   */
  async createBackup(): Promise<string> {
    const response = await this.makeRequest<{ backup_name: string }>('/maintenance/backup', {
      method: 'POST',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Erreur lors de la création de la sauvegarde');
    }

    return response.data.backup_name;
  }

  /**
   * Nettoie les données temporaires
   */
  async cleanupSystem(): Promise<string[]> {
    const response = await this.makeRequest<{ cleaned_items: string[] }>('/maintenance/cleanup', {
      method: 'POST',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Erreur lors du nettoyage du système');
    }

    return response.data.cleaned_items;
  }

  // ==================== UTILITAIRES ====================

  /**
   * Vérifie si l'utilisateur actuel a les droits admin
   */
  isAdmin(): boolean {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      
      const user = JSON.parse(userStr);
      return user.role === 'admin';
    } catch {
      return false;
    }
  }

  /**
   * Récupère les informations de l'utilisateur actuel
   */
  getCurrentUser(): AdminUser | null {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;
      
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Formate une date pour l'affichage
   */
  formatDate(dateString: string): string {
    try {
      return new Date(dateString).toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Formate un nombre pour l'affichage
   */
  formatNumber(num: number): string {
    return new Intl.NumberFormat('fr-FR').format(num);
  }

  /**
   * Génère une couleur pour les graphiques basée sur un index
   */
  getChartColor(index: number): string {
    const colors = [
      '#3B82F6', // Bleu
      '#10B981', // Vert
      '#F59E0B', // Orange
      '#EF4444', // Rouge
      '#8B5CF6', // Violet
      '#06B6D4', // Cyan
      '#84CC16', // Lime
      '#F97316', // Orange foncé
    ];
    return colors[index % colors.length];
  }
}

// Instance singleton du service
export const adminService = new AdminService();
export default adminService;
